import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  LogIn, LogOut, Clock, FileText, Download, Lock, ArrowLeft, Eye, Trash2
} from "lucide-react";

import { API_URL } from "../lib/utils";

function LoginForm({ onLogin }) {
  const [cf, setCf] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/portal/login`, {
        codice_fiscale: cf, pin
      });
      onLogin(res.data, cf);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#F4F4F5] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border border-[#E4E4E7] shadow-lg">
        <CardHeader className="text-center pb-2 px-5 pt-6">
          <CardTitle className="font-outfit text-2xl">Area Dipendenti</CardTitle>
          <p className="text-sm text-[#52525B] font-ibm-plex mt-1">Accedi con il tuo codice fiscale</p>
        </CardHeader>
        <CardContent className="px-5 pb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-xs font-ibm-plex font-medium">Codice Fiscale</label>
              <Input
                data-testid="portal-cf-input"
                value={cf}
                onChange={(e) => setCf(e.target.value.toUpperCase())}
                placeholder="RSSMRA85M01H501Z"
                className="font-ibm-plex-mono text-base tracking-wider h-12"
                maxLength={16}
                autoComplete="off"
                autoCapitalize="characters"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-ibm-plex font-medium">PIN (6 cifre)</label>
              <Input
                data-testid="portal-pin-input"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="------"
                className="font-ibm-plex-mono text-center text-2xl tracking-[0.5em] h-14"
                maxLength={6}
              />
            </div>
            {error && <p className="text-red-500 text-sm font-ibm-plex text-center">{error}</p>}
            <Button
              type="submit"
              disabled={loading || cf.length < 10 || pin.length < 6}
              className="w-full bg-[#09090B] hover:bg-[#27272A] text-white font-ibm-plex h-12 text-base"
              data-testid="portal-login-btn"
            >
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function ChangePinDialog({ open, cf, onDone }) {
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = async () => {
    setError("");
    if (newPin !== confirmPin) { setError("I PIN non corrispondono"); return; }
    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) { setError("Il PIN deve essere di 6 cifre"); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/portal/change-pin`, {
        codice_fiscale: cf, old_pin: oldPin, new_pin: newPin
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-[90vw] sm:max-w-sm mx-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2 text-lg">
            <Lock className="w-5 h-5" /> Cambio PIN obbligatorio
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-[#52525B] font-ibm-plex">Per sicurezza, imposta un nuovo PIN personale.</p>
          <div className="space-y-1.5">
            <label className="text-xs font-ibm-plex font-medium">PIN attuale</label>
            <Input type="password" inputMode="numeric" pattern="[0-9]*" value={oldPin} onChange={(e) => setOldPin(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} className="font-ibm-plex-mono text-center text-xl tracking-[0.5em] h-12" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-ibm-plex font-medium">Nuovo PIN</label>
            <Input type="password" inputMode="numeric" pattern="[0-9]*" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} className="font-ibm-plex-mono text-center text-xl tracking-[0.5em] h-12" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-ibm-plex font-medium">Conferma nuovo PIN</label>
            <Input type="password" inputMode="numeric" pattern="[0-9]*" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} className="font-ibm-plex-mono text-center text-xl tracking-[0.5em] h-12" />
          </div>
          {error && <p className="text-red-500 text-sm font-ibm-plex">{error}</p>}
        </div>
        <DialogFooter>
          <Button onClick={handleChange} disabled={loading || newPin.length < 6} className="w-full bg-[#09090B] hover:bg-[#27272A] text-white font-ibm-plex h-12 text-base">
            {loading ? "Salvataggio..." : "Imposta nuovo PIN"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PortalDashboard({ session, cf, onLogout }) {
  const [clocks, setClocks] = useState([]);
  const [docs, setDocs] = useState({ personal: [], shared: [] });
  const [tab, setTab] = useState("timbrature");
  const [clockLoading, setClockLoading] = useState(false);
  const [clockTime, setClockTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const today = new Date().toISOString().split("T")[0];

  const loadClocks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/portal/clocks/${session.employee_id}`, { params: { date: today } });
      setClocks(res.data);
    } catch (err) { console.error(err); }
  }, [session.employee_id, today]);

  const loadDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/portal/documents/${session.employee_id}`);
      setDocs(res.data);
    } catch (err) { console.error(err); }
  }, [session.employee_id]);

  useEffect(() => { loadClocks(); loadDocs(); }, [loadClocks, loadDocs]);

  const handleClock = async (type) => {
    setClockLoading(true);
    try {
      await axios.post(`${API_URL}/api/portal/clock`, {
        employee_id: session.employee_id, type, time: clockTime
      });
      loadClocks();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setClockLoading(false);
    }
  };

  const handleDeleteClock = async (clockId) => {
    try {
      await axios.delete(`${API_URL}/api/portal/clocks/${clockId}`);
      loadClocks();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const formatTime = (c) => c.time || new Date(c.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  const lastClock = clocks.length > 0 ? clocks[0] : null;
  const isIn = lastClock?.type === "in";

  return (
    <div className="min-h-[100dvh] bg-[#F4F4F5] flex flex-col">
      {/* Header */}
      <div className="bg-[#09090B] text-white px-4 py-3 flex items-center justify-between shrink-0 safe-area-top">
        <div className="min-w-0">
          <p className="font-outfit font-semibold text-base">Area Dipendenti</p>
          <p className="text-xs text-[#A1A1AA] font-ibm-plex truncate">{session.name}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onLogout} className="text-white hover:text-[#A1A1AA] text-xs shrink-0">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Esci
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E4E4E7] bg-white shrink-0">
        <button
          className={`flex-1 py-3.5 text-sm font-ibm-plex font-medium flex items-center justify-center gap-1.5 ${tab === "timbrature" ? "border-b-2 border-[#09090B] text-[#09090B]" : "text-[#71717A]"}`}
          onClick={() => setTab("timbrature")}
          data-testid="portal-tab-timbrature"
        >
          <Clock className="w-4 h-4" /> Timbrature
        </button>
        <button
          className={`flex-1 py-3.5 text-sm font-ibm-plex font-medium flex items-center justify-center gap-1.5 ${tab === "documenti" ? "border-b-2 border-[#09090B] text-[#09090B]" : "text-[#71717A]"}`}
          onClick={() => setTab("documenti")}
          data-testid="portal-tab-documenti"
        >
          <FileText className="w-4 h-4" /> Documenti
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 pb-8 w-full max-w-lg mx-auto space-y-4">
          {tab === "timbrature" && (
            <>
              {/* Time input */}
              <div className="flex flex-col items-center gap-2 pt-2">
                <label className="font-ibm-plex text-sm text-[#52525B]">Inserisci orario</label>
                <Input
                  type="time"
                  value={clockTime}
                  onChange={(e) => setClockTime(e.target.value)}
                  className="font-ibm-plex-mono text-center text-3xl sm:text-4xl h-16 sm:h-20 w-48 sm:w-56 tracking-wider border-2 border-[#D4D4D8] rounded-xl"
                  data-testid="portal-clock-time"
                />
              </div>

              {/* Clock buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  data-testid="portal-clock-in"
                  onClick={() => handleClock("in")}
                  disabled={clockLoading}
                  className="h-24 sm:h-28 bg-[#10B981] hover:bg-[#059669] active:bg-[#047857] text-white font-ibm-plex text-lg sm:text-xl flex flex-col gap-2 rounded-xl shadow-md"
                >
                  <LogIn className="w-8 h-8" />
                  Entrata
                </Button>
                <Button
                  data-testid="portal-clock-out"
                  onClick={() => handleClock("out")}
                  disabled={clockLoading}
                  className="h-24 sm:h-28 bg-[#EF4444] hover:bg-[#DC2626] active:bg-[#B91C1C] text-white font-ibm-plex text-lg sm:text-xl flex flex-col gap-2 rounded-xl shadow-md"
                >
                  <LogOut className="w-8 h-8" />
                  Uscita
                </Button>
              </div>

              {/* Status */}
              {lastClock && (
                <div className={`text-center py-3 rounded-xl text-base font-ibm-plex ${isIn ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                  Ultimo: <strong>{lastClock.type === "in" ? "Entrata" : "Uscita"}</strong> alle {formatTime(lastClock)}
                </div>
              )}

              {/* Today's clocks */}
              <Card className="border border-[#E4E4E7] rounded-xl">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-ibm-plex font-semibold">Timbrature di oggi</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 px-4 pb-3">
                  {clocks.length === 0 ? (
                    <p className="text-sm text-[#71717A] font-ibm-plex text-center py-6">Nessuna timbratura</p>
                  ) : (
                    <div className="space-y-0">
                      {clocks.slice().reverse().map((c) => (
                        <div key={c.id} className="flex items-center justify-between py-3 border-b border-[#F4F4F5] last:border-0">
                          <div className="flex items-center gap-3">
                            {c.type === "in" ? (
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <LogIn className="w-4 h-4 text-green-600" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                <LogOut className="w-4 h-4 text-red-600" />
                              </div>
                            )}
                            <span className="font-ibm-plex text-base">{c.type === "in" ? "Entrata" : "Uscita"}</span>
                          </div>
                          <span className="font-ibm-plex-mono text-lg font-medium">{formatTime(c)}</span>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => setDeleteConfirm(c)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {tab === "documenti" && (
            <>
              {docs.personal.length > 0 && (
                <Card className="border border-[#E4E4E7] rounded-xl">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-ibm-plex font-semibold">I miei documenti</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-3">
                    <div className="space-y-0">
                      {docs.personal.map((d) => (
                        <div key={d.id} className="flex items-center justify-between py-3 border-b border-[#F4F4F5] last:border-0 gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-ibm-plex text-sm font-medium truncate">{d.description || d.filename}</p>
                            <p className="text-xs text-[#71717A] font-ibm-plex truncate">{d.filename}</p>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <a href={`${API_URL}/api/portal/documents/preview/${d.id}`} target="_blank" rel="noreferrer">
                              <Button variant="ghost" size="icon" className="h-10 w-10">
                                <Eye className="w-5 h-5" />
                              </Button>
                            </a>
                            <a href={`${API_URL}/api/portal/documents/download/${d.id}`} target="_blank" rel="noreferrer">
                              <Button variant="ghost" size="icon" className="h-10 w-10">
                                <Download className="w-5 h-5" />
                              </Button>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {docs.shared.length > 0 && (
                <Card className="border border-[#E4E4E7] rounded-xl">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-ibm-plex font-semibold">Comunicazioni aziendali</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-3">
                    <div className="space-y-0">
                      {docs.shared.map((d) => (
                        <div key={d.id} className="flex items-center justify-between py-3 border-b border-[#F4F4F5] last:border-0 gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-ibm-plex text-sm font-medium truncate">{d.description || d.filename}</p>
                            <p className="text-xs text-[#71717A] font-ibm-plex truncate">{d.filename}</p>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <a href={`${API_URL}/api/portal/documents/preview/${d.id}`} target="_blank" rel="noreferrer">
                              <Button variant="ghost" size="icon" className="h-10 w-10">
                                <Eye className="w-5 h-5" />
                              </Button>
                            </a>
                            <a href={`${API_URL}/api/portal/documents/download/${d.id}`} target="_blank" rel="noreferrer">
                              <Button variant="ghost" size="icon" className="h-10 w-10">
                                <Download className="w-5 h-5" />
                              </Button>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {docs.personal.length === 0 && docs.shared.length === 0 && (
                <div className="text-center py-12 text-[#71717A] font-ibm-plex text-sm">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-[#D4D4D8]" />
                  Nessun documento disponibile
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-[90vw] sm:max-w-xs mx-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-base">Conferma eliminazione</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#52525B] font-ibm-plex py-2">
            Vuoi eliminare la timbratura <strong>{deleteConfirm?.type === "in" ? "Entrata" : "Uscita"}</strong> delle ore <strong>{deleteConfirm ? formatTime(deleteConfirm) : ""}</strong>?
          </p>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="font-ibm-plex flex-1">Annulla</Button>
            <Button onClick={() => handleDeleteClock(deleteConfirm.id)} className="bg-red-600 hover:bg-red-700 text-white font-ibm-plex flex-1">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function EmployeePortalPage() {
  const [session, setSession] = useState(null);
  const [cf, setCf] = useState("");
  const [mustChangePin, setMustChangePin] = useState(false);

  const handleLogin = (data, codiceFiscale) => {
    setSession(data);
    setCf(codiceFiscale);
    setMustChangePin(data.must_change_pin);
  };

  const handlePinChanged = () => {
    setMustChangePin(false);
  };

  const handleLogout = () => {
    setSession(null);
    setCf("");
    setMustChangePin(false);
  };

  if (!session) return <LoginForm onLogin={handleLogin} />;

  return (
    <>
      {mustChangePin && <ChangePinDialog open={true} cf={cf} onDone={handlePinChanged} />}
      <PortalDashboard session={session} cf={cf} onLogout={handleLogout} />
    </>
  );
}
